/**
 * The access state machine of the browser, as pure functions.
 *
 * The React components hold the wiring only. Every decision — which screen a
 * signed-in user sees, and whether a denied request blanks the open tab —
 * lives here, so a test can run it without a DOM.
 */

import { sanitizeTabKeys, type TabKey } from "./tab-registry";

/**
 * `loading` is the session read in flight. `none` is a signed-out browser.
 * `unavailable` is the auth service failing: the user keeps the session they
 * have, and the dashboard says so instead of asking them to sign in again.
 */
export type AuthStatus = "loading" | "ok" | "none" | "unavailable";

/** Whether et-auth could read the tab grants of an admin session. */
export type PermissionsStatus = "ok" | "unavailable";

/** The three states `GET /api/auth/status` answers. */
export type AuthStatusProbe = "ok" | "none" | "unavailable";

export interface AuthState {
  authStatus: AuthStatus;
  permissionsStatus: PermissionsStatus;
  /** Tabs the user may view. null only while the session is loading. */
  allowedTabs: TabKey[] | null;
}

export interface AuthStateInput {
  /** better-auth still reading the session. */
  isLoading: boolean;
  hasUser: boolean;
  isAdmin: boolean;
  /** `permissions` off the session root. */
  permissions: unknown;
  /** `permissionsStatus` off the session root. */
  sessionPermissionsStatus: unknown;
  /** Answer of the status probe, or null while it is in flight. */
  probe: AuthStatusProbe | null;
}

/**
 * Turn a better-auth session plus the status probe into the state the screens
 * read. An admin session whose grants could not be read reports
 * `permissionsStatus: "unavailable"` and zero tabs, because an empty list
 * would read as a revoke.
 */
export const resolveAuthState = (input: AuthStateInput): AuthState => {
  if (input.isLoading) {
    return { authStatus: "loading", permissionsStatus: "ok", allowedTabs: null };
  }

  if (!input.hasUser) {
    // "ok" with no user in hand means the browser's session read lost a race
    // with a recovery; the provider reads the session once more (see
    // `settleProbe`), and the screen waits for that instead of showing login.
    const authStatus: AuthStatus =
      input.probe === null || input.probe === "ok"
        ? "loading"
        : input.probe === "unavailable"
          ? "unavailable"
          : "none";
    return {
      authStatus,
      permissionsStatus: "ok",
      allowedTabs: authStatus === "loading" ? null : [],
    };
  }

  if (!input.isAdmin) {
    return { authStatus: "ok", permissionsStatus: "ok", allowedTabs: [] };
  }

  const grantsReadable =
    input.sessionPermissionsStatus === "ok" && Array.isArray(input.permissions);
  if (!grantsReadable) {
    return { authStatus: "ok", permissionsStatus: "unavailable", allowedTabs: [] };
  }

  return {
    authStatus: "ok",
    permissionsStatus: "ok",
    allowedTabs: sanitizeTabKeys(input.permissions),
  };
};

/** What the protected route renders. */
export type ProtectedScreen =
  | "loading"
  | "outage"
  | "login"
  | "not-dashboard-account"
  | "access-denied"
  | "content";

export interface ProtectedScreenInput {
  authStatus: AuthStatus;
  permissionsStatus: PermissionsStatus;
  isAdmin: boolean;
  allowedTabs: TabKey[] | null;
}

/**
 * Pick the screen. The outage screen wins over both "not a dashboard account"
 * and "no tabs granted yet": while the check is broken the dashboard knows
 * nothing about the account, so it must not accuse it of anything.
 */
export const chooseProtectedScreen = (input: ProtectedScreenInput): ProtectedScreen => {
  if (input.authStatus === "loading") return "loading";
  if (input.authStatus === "unavailable") return "outage";
  if (input.authStatus === "none") return "login";
  if (input.allowedTabs === null) return "loading";
  if (!input.isAdmin) return "not-dashboard-account";
  if (input.permissionsStatus !== "ok") return "outage";
  if (input.allowedTabs.length === 0) return "access-denied";
  return "content";
};

/**
 * Turn a probe answer, given while the browser holds no user, into the state
 * to show. "ok" means the server sees a session the browser's read missed:
 * read the session once more past the cookie cache. A user that arrives
 * changes the provider's state and cancels this; if none arrives, the answer
 * is "signed out". Once per probe, so a server that keeps saying "ok" to a
 * browser that keeps finding nobody cannot spin. Null means "cancelled".
 */
export const settleProbe = async (
  status: AuthStatusProbe,
  reread: () => Promise<unknown>,
  isCancelled: () => boolean,
): Promise<AuthStatusProbe | null> => {
  if (status !== "ok") return status;
  try {
    await reread();
  } catch {
    // The re-read itself failed: the check cannot answer, so the screen shows
    // the outage with its Retry instead of loading forever.
    return isCancelled() ? null : "unavailable";
  }
  return isCancelled() ? null : "none";
};

export interface DenyScope {
  /** The tab named by the 403 body, when it named one. */
  tab?: string;
  /** The tabs of an "any of these" guard. */
  tabs?: string[];
  /** The tab the user is looking at. */
  activeTab: string;
}

/**
 * Does this 403 mean the open tab is gone?
 *
 * A tab makes calls to routes of other tabs — the prompts tab reads the
 * organization list, which the organizations guard covers. A deny on such a
 * subordinate call says nothing about the tab the user holds, so it must not
 * blank it.
 *
 * A guard denies only a caller who holds none of the tabs it names. So a deny
 * that names the open tab, alone or among others, proves the open tab is
 * gone; a deny that does not name it says nothing about it.
 */
export const denyBlanksActiveTab = ({ tab, tabs, activeTab }: DenyScope): boolean => {
  const named = [...(tab ? [tab] : []), ...(tabs ?? [])];
  // A body that names no tab is the guard of the open tab itself.
  if (named.length === 0) return true;
  return named.includes(activeTab);
};
